import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { FolderTree, Loader2, Pencil, Plus, Users as UsersIcon, Trash2 } from 'lucide-react';

import CompanyAdminLayout from '../../components/CompanyAdminLayout';
import { useCompanyAdminAuth } from '../../contexts/CompanyAdminAuthContext';
import { companyAdminCustomersAPI } from '../../lib/companyAdminApi';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Button } from '../../components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';

const StatusBadge = ({ status }) => {
  const active = Number(status) === 1;
  return (
    <span className={active ? 'text-green-600 font-semibold' : 'text-red-600 font-semibold'}>
      {active ? 'Active' : 'Inactive'}
    </span>
  );
};

const ModalShell = ({ open, title, children, onClose }) => {
  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
      onClick={onClose}
      role="presentation"
    >
      <div
        className="panel w-full max-w-2xl mx-4 p-0 app-modal-shell"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="app-modal-header flex items-center justify-between px-6 py-4 border-b border-border">
          <div className="text-lg font-semibold text-foreground">{title}</div>
          <button className="text-muted-foreground hover:text-foreground" onClick={onClose} aria-label="Close">✕</button>
        </div>
        <div className="app-modal-body p-6">{children}</div>
      </div>
    </div>
  );
};

const UsersModal = ({ open, onClose, users }) => {
  return (
    <ModalShell open={open} onClose={onClose} title="Assigned Users">
      {users?.length ? (
        <div className="max-h-[420px] min-h-0 overflow-y-auto overflow-x-auto border border-border rounded-md">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>User Type</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {users.map((u) => (
                <TableRow key={u.user_id}>
                  <TableCell className="font-medium text-foreground">{u.name || '-'}</TableCell>
                  <TableCell className="text-muted-foreground">{u.email}</TableCell>
                  <TableCell><StatusBadge status={u.status} /></TableCell>
                  <TableCell className="text-muted-foreground">{Number(u.user_type) === 1 ? 'Admin' : Number(u.user_type) === 2 ? 'Manager' : Number(u.user_type) === 3 ? 'Agent' : Number(u.user_type) === 4 ? 'Customer' : 'Unknown'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      ) : (
        <div className="text-sm text-muted-foreground">No users assigned.</div>
      )}
      <div className="flex justify-end mt-4">
        <Button variant="outline" onClick={onClose}>Close</Button>
      </div>
    </ModalShell>
  );
};

const GroupForm = ({ mode, open, onClose, initial, onSaved }) => {
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ name: '', status: 1, is_default: false });

  useEffect(() => {
    if (!open) return;
    setForm({
      name: initial?.name || '',
      status: Number(initial?.status ?? 1),
      is_default: Boolean(initial?.is_default),
    });
  }, [open, initial]);

  const handleSave = async () => {
    if (!form.name.trim()) {
      toast.error('Group name is required');
      return;
    }

    setSaving(true);
    try {
      if (mode === 'add') {
        await companyAdminCustomersAPI.createGroup(form);
        toast.success('Group created');
      } else {
        await companyAdminCustomersAPI.updateGroup(initial.id, form);
        toast.success('Group updated');
      }
      onSaved?.();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <ModalShell open={open} onClose={onClose} title={mode === 'add' ? 'Add Group' : 'Edit Group'}>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <Label className="text-foreground">Group Name</Label>
          <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        </div>
        <div>
          <Label className="text-foreground">Status</Label>
          <select className="form-select" value={form.status} onChange={(e) => setForm({ ...form, status: Number(e.target.value) })}>
            <option value={1}>Active</option>
            <option value={0}>Inactive</option>
          </select>
        </div>
        <div>
          <Label className="text-foreground">Default</Label>
          <select className="form-select" value={form.is_default ? 1 : 0} onChange={(e) => setForm({ ...form, is_default: Number(e.target.value) === 1 })}>
            <option value={1}>Yes</option>
            <option value={0}>No</option>
          </select>
        </div>
      </div>

      <div className="flex justify-end gap-2 mt-6">
        <Button variant="outline" onClick={onClose}>Cancel</Button>
        <Button onClick={handleSave} disabled={saving}>
          {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
          Save
        </Button>
      </div>
    </ModalShell>
  );
};

const GroupsPage = () => {
  const { user, canAction } = useCompanyAdminAuth();
  const canAdd    = canAction('groups', 'add');
  const canEdit   = canAction('groups', 'edit');
  const canDelete = canAction('groups', 'delete');
  const isManager = Number(user?.user_type) === 2;

  const [loading, setLoading] = useState(true);
  const [groups, setGroups] = useState([]);

  const [addOpen, setAddOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editGroup, setEditGroup] = useState(null);

  const [usersModalOpen, setUsersModalOpen] = useState(false);
  const [usersModalData, setUsersModalData] = useState([]);

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteGroup, setDeleteGroup] = useState(null);
  const [moveToGroupId, setMoveToGroupId] = useState('');

  const confirmDelete = async () => {
    if (!deleteGroup) return;
    if (!moveToGroupId) {
      toast.error('Please select another group');
      return;
    }

    try {
      await companyAdminCustomersAPI.deleteGroup(deleteGroup.id, { move_to_group_id: Number(moveToGroupId) });
      toast.success('Group deleted');
      setDeleteOpen(false);
      setDeleteGroup(null);
      setMoveToGroupId('');
      await fetchGroups();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    }
  };

  const fetchGroups = async () => {
    setLoading(true);
    try {
      // view should include inactive too
      const res = await companyAdminCustomersAPI.listGroups({ include_inactive: true });
      setGroups(res.data.groups || []);
    } catch {
      toast.error('Failed to load groups');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchGroups();
    // eslint-disable-next-line
  }, []);

  const openUsersModal = async (group) => {
    try {
      const res = await companyAdminCustomersAPI.listGroupUsers(group.id);
      setUsersModalData(res.data.users || []);
      setUsersModalOpen(true);
    } catch {
      toast.error('Failed to load group users');
    }
  };

  return (
    <CompanyAdminLayout>
      <div className="flex items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-foreground flex items-center gap-2"><FolderTree className="w-5 h-5" /> Groups</h1>
          <p className="text-sm text-muted-foreground mt-1">Create groups and manage assignments.</p>
        </div>
        {canAdd || canEdit || canDelete && (
          <Button onClick={() => setAddOpen(true)}>
            <Plus className="w-4 h-4 mr-2" />
            Add Group
          </Button>
        )}
      </div>

      <div className="panel border border-border">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-primary" />
          </div>
        ) : (
          <div className="overflow-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Group Name</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Default</TableHead>
                  <TableHead>Users</TableHead>
                  <TableHead className="text-right">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {groups.map((g) => (
                  <TableRow key={g.id}>
                    <TableCell className="font-medium text-foreground">{g.name}</TableCell>
                    <TableCell><StatusBadge status={g.status} /></TableCell>
                    <TableCell className={g.is_default ? 'text-green-600 font-semibold' : 'text-red-600 font-semibold'}>{g.is_default ? 'Yes' : 'No'}</TableCell>
                    <TableCell>
                      <button
                        onClick={() => openUsersModal(g)}
                        className="text-primary text-sm font-semibold hover:underline"
                      >
                        {Number(g.users_count ?? 0)}
                      </button>
                    </TableCell>
                    <TableCell className="text-right">
                      {canAdd || canEdit || canDelete && (
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="hover:bg-muted"
                            onClick={() => {
                              setEditGroup(g);
                              setEditOpen(true);
                            }}
                            aria-label="Edit"
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="hover:bg-muted"
                            onClick={() => {
                              setDeleteGroup(g);
                              setMoveToGroupId('');
                              setDeleteOpen(true);
                            }}
                            aria-label="Delete"
                          >
                            <Trash2 className="w-4 h-4 text-red-600" />
                          </Button>
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      <ModalShell open={deleteOpen} onClose={() => setDeleteOpen(false)} title="Delete Group">
        <div className="text-sm text-muted-foreground">
          Users and customers exist in this group. Please select another group to move them.
        </div>

        <div className="mt-4">
          <Label className="text-foreground">Select Group</Label>
          <select
            className="form-select mt-1"
            value={moveToGroupId}
            onChange={(e) => setMoveToGroupId(e.target.value)}
          >
            <option value="">Select Group</option>
            {(groups || [])
              .filter((x) => Number(x.status) === 1 && Number(x.id) !== Number(deleteGroup?.id))
              .map((g) => (
                <option key={g.id} value={g.id}>
                  {g.name}
                </option>
              ))}
          </select>
        </div>

        <div className="flex justify-end gap-2 mt-6">
          <Button variant="outline" onClick={() => setDeleteOpen(false)}>
            Cancel
          </Button>
          <Button
            onClick={confirmDelete}
            disabled={!moveToGroupId}
            className="bg-red-600 hover:bg-red-700 text-white"
          >
            Yes, Delete
          </Button>
        </div>
      </ModalShell>

      <UsersModal open={usersModalOpen} onClose={() => setUsersModalOpen(false)} users={usersModalData} />

      {canAdd || canEdit || canDelete && (
        <GroupForm mode="add" open={addOpen} onClose={() => setAddOpen(false)} onSaved={fetchGroups} />
      )}
      {canAdd || canEdit || canDelete && (
        <GroupForm mode="edit" open={editOpen} onClose={() => setEditOpen(false)} initial={editGroup} onSaved={fetchGroups} />
      )}
    </CompanyAdminLayout>
  );
};

export default GroupsPage;
